import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { authApi } from '../../lib/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA');
      return;
    }

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authApi.forgotPassword({ 
        email,
        'g-recaptcha-response': recaptchaToken
      });
      
      if (response.success) {
        setSuccess(response.message || 'Password reset link sent successfully');
        setIsSubmitted(true);
      } else {
        setError(response.message || 'Failed to send password reset email');
      }
    } catch (err) {
      setError('Network error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    if (error && error.includes('reCAPTCHA')) {
      setError(null);
    }
  };

  if (isSubmitted) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-0">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">Check Your Email</h1>
          <p className="text-black text-base sm:text-lg">
            We've sent a password reset link to your email address.
          </p>
        </div>

        {/* Success Message */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-green-800 text-sm">
            {success}
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <p className="text-gray-700 text-sm mb-2">
            <strong>What to do next:</strong>
          </p>
          <ul className="text-gray-600 text-sm space-y-1">
            <li>• Check your email inbox (and spam folder)</li>
            <li>• Click the password reset link in the email</li>
            <li>• Create a new password</li>
            <li>• Sign in with your new password</li>
          </ul>
        </div>

        {/* Back to Sign In Link */}
        <div className="text-center">
          <Link
            to="/auth/login"
            className="text-secondary-500 hover:text-secondary-600 font-medium"
          >
            ← Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-0">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">Forgot Password</h1>
        <p className="text-black text-base sm:text-lg">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Forgot Password Form */}
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-black mb-2">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-sm sm:text-base"
            placeholder="Enter your email address"
            required
            disabled={isLoading}
          />
        </div>

        {/* reCAPTCHA */}
        <div className="flex justify-center">
          <ReCAPTCHA
            sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
            onChange={handleRecaptchaChange}
            theme="light"
            size="normal"
          />
        </div>

        {/* Send Reset Link Button */}
        <button
          type="submit"
          disabled={isLoading || !recaptchaToken}
          className="w-full bg-secondary-500 text-white py-2.5 sm:py-3 px-4 rounded-lg font-medium hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 transition-colors text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Sending...
            </div>
          ) : (
            'Send Reset Link'
          )}
        </button>

        {/* Back to Sign In Link */}
        <div className="text-center">
          <Link
            to="/auth/login"
            className="text-secondary-500 hover:text-secondary-600 font-medium"
          >
            ← Back to Sign In
          </Link>
        </div>
      </form>
    </div>
  );
}
